import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { chunk } from 'lodash';
import DomainCard from './DomainCard';
import UltraPremiumDomainCard from './UltraPremiumDomainCard';
import CategoryTab from './CategoryTab';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import RequestForm from './RequestForm';
import domainsData from '../data/domainsData';

const DomainCardsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [requestedDomain, setRequestedDomain] = useState(null); // For showing the form

  // Filter domains based on category and search term.
  const filteredDomains = useMemo(() => {
    return domainsData.filter(domain => {
      const matchesCategory =
        selectedCategory === 'all' || domain.category === selectedCategory;
      const matchesSearch = domain.domain.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory]);

  // Sort domains: ultra premium first, then premium, then the rest.
  const sortedDomains = useMemo(() => {
    return filteredDomains.sort((a, b) => {
      const rank = (domain) => {
        if (domain.ultraPremium) return 0;
        if (domain.isPremium) return 1;
        return 2;
      };
      return rank(a) - rank(b);
    });
  }, [filteredDomains]);

  // Paginate sorted domains into chunks of 9.
  const paginatedDomains = useMemo(() => chunk(sortedDomains, 9), [sortedDomains]);

  // Calculate counts per category for tabs.
  const categoryCounts = useMemo(() => {
    const counts = domainsData.reduce((acc, domain) => {
      acc[domain.category] = (acc[domain.category] || 0) + 1;
      return acc;
    }, {});
    counts.all = domainsData.length;
    return counts;
  }, []);

  const categories = [
    { id: 'all', label: 'All Domains' },
    { id: 'ultra', label: 'Ultra Premium' },
    { id: 'premium', label: 'Premium' },
    { id: 'AI & Crypto', label: 'AI & Crypto' },
    { id: 'Usecase & Tech', label: 'Usecase & Tech' },
    { id: 'Community & Companies', label: 'Community & Companies' }
  ];

  // Callback to handle request button clicks
  const handleRequest = (domainName) => {
    setRequestedDomain(domainName);
  };

  // Callback to close the request form
  const closeRequestForm = () => {
    setRequestedDomain(null);
  };

  return (
    <div className="min-h-screen bg-black py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center space-y-4">
          <h2 className="text-4xl font-medium text-white">Premium Domains</h2>
          <div className="h-px w-20 mx-auto bg-gradient-to-r from-transparent via-gray-500 to-transparent" />
          <p className="text-gray-400 max-w-2xl mx-auto">
            Secure your digital legacy with our exclusive collection
          </p>
        </div>
        <div className="space-y-8">
          <SearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <CategoryTab
                key={category.id}
                category={category.label}
                active={selectedCategory === category.id}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setCurrentPage(0);
                }}
                count={categoryCounts[category.id] || 0}
              />
            ))}
          </div>
        </div>
        <div
          className={
            selectedCategory === 'all'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
              : 'grid grid-cols-1 gap-8'
          }
        >
          <AnimatePresence>
            {paginatedDomains[currentPage]?.map((domain, index) => (
              <motion.div
                key={domain.domain}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                {domain.ultraPremium ? (
                  <UltraPremiumDomainCard 
                    domain={domain.domain} 
                    price={domain.price}
                    currency={domain.currency || "ETH"} 
                    onRequest={handleRequest}  // Pass callback here
                  />
                ) : (
                  <DomainCard 
                    {...domain} 
                    onRequest={handleRequest}  // Make sure DomainCard supports this prop
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        {paginatedDomains.length > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={paginatedDomains.length}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
        {sortedDomains.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400">No domains found</div>
          </div>
        )}
      </div>
      {/* Render the RequestForm modal if a domain is selected */}
      {requestedDomain && (
        <RequestForm domain={requestedDomain} onClose={closeRequestForm} />
      )}
    </div>
  );
};

export default DomainCardsSection;